import React, { useState } from 'react';
import { Checkbox, Field, FieldError, NumberInput } from 'ui';
import {
  ListFlowParcel,
  isListFlowUsingCustomDimensions,
  toggleRequiresCustomDimensions,
} from '~/store/listFlow/slices/shipping';
import { getListFlowFieldErrorMap } from '~/store/listFlow/ui';
import { useAppDispatch, useAppSelector } from '~/store/store';

function getOunces(weight?: number): number {
  if (weight != null) {
    const decimal = Number(weight.toFixed(2).split('.')[1]);

    if (decimal) {
      return Number(((decimal / 100) * 16).toFixed(0));
    }
  }
  return 0;
}

interface ParcelWithOunces extends ListFlowParcel {
  weightOz?: number;
}

interface Props {
  onChangeParcel: (parcel: ListFlowParcel) => void;
  parcel?: ListFlowParcel;
}

const ImperialParcelDetails: React.FC<Props> = ({ onChangeParcel, parcel }) => {
  const dispatch = useAppDispatch();
  const requiresCustomDimensions = useAppSelector(
    isListFlowUsingCustomDimensions,
  );
  const fieldErrors = useAppSelector(getListFlowFieldErrorMap);

  const [localParcel, setLocalParcel] = useState<ParcelWithOunces>(() => ({
    ...parcel,
    weight: Math.floor(parcel?.weight ?? 0),
    weightOz: getOunces(parcel?.weight ?? 0) ?? 0,
    lockedDimensions: false,
  }));

  function commitToState() {
    onChangeParcel({
      length: localParcel.length,
      width: localParcel.width,
      height: localParcel.height,
      weight:
        (localParcel.weight ?? 0) +
        (localParcel.weightOz ? localParcel.weightOz / 16 : 0),
      lockedDimensions: localParcel.lockedDimensions,
    });
  }

  function handleChange<T extends keyof ParcelWithOunces>(
    field: T,
    value: ParcelWithOunces[T],
  ) {
    setLocalParcel(prev => ({
      ...prev,
      [field]: value,
    }));
  }

  return (
    <div>
      <div className="flex space-x-4">
        <Field label="Weight (lbs)" id="weight" error={!!fieldErrors?.weight}>
          <NumberInput
            id="weight"
            decimalScale={0}
            value={localParcel.weight ?? ''}
            onBlur={commitToState}
            onValueChange={v =>
              handleChange('weight', v.floatValue ?? undefined)
            }
            rightAdornment={<span className="text-grey-700 pr-4">lbs</span>}
          />
          {fieldErrors?.weight && (
            <FieldError>{fieldErrors?.weight}</FieldError>
          )}
        </Field>

        <Field label="Ounces" id="ounces">
          <NumberInput
            id="ounces"
            decimalScale={0}
            value={localParcel.weightOz ?? ''}
            onBlur={commitToState}
            onValueChange={v =>
              handleChange('weightOz', v.floatValue ?? undefined)
            }
            rightAdornment={<span className="text-grey-700 pr-4">oz</span>}
          />
        </Field>
      </div>

      <label className="mb-4 flex items-center space-x-2">
        <Checkbox
          checked={requiresCustomDimensions}
          onChange={e =>
            dispatch(
              toggleRequiresCustomDimensions({ enabled: e.target.checked }),
            )
          }
        />
        <span>Is this package longer than 72 inches?</span>
      </label>

      {requiresCustomDimensions && (
        <div className="flex space-x-4">
          <Field label="Length" id="length">
            <NumberInput
              id="length"
              decimalScale={0}
              value={localParcel.length ?? ''}
              onBlur={commitToState}
              onValueChange={v =>
                handleChange('length', v.floatValue ?? undefined)
              }
              rightAdornment={<span className="text-grey-700 pr-4">in</span>}
            />
          </Field>

          <Field label="Width" id="width">
            <NumberInput
              id="width"
              decimalScale={0}
              value={localParcel.width ?? ''}
              onBlur={commitToState}
              onValueChange={v =>
                handleChange('width', v.floatValue ?? undefined)
              }
              rightAdornment={<span className="text-grey-700 pr-4">in</span>}
            />
          </Field>

          <Field label="Height" id="height">
            <NumberInput
              id="height"
              decimalScale={0}
              value={localParcel.height ?? ''}
              onBlur={commitToState}
              onValueChange={v =>
                handleChange('height', v.floatValue ?? undefined)
              }
              rightAdornment={<span className="text-grey-700 pr-4">in</span>}
            />
          </Field>
        </div>
      )}
    </div>
  );
};

export default ImperialParcelDetails;
